import { Injectable, inject } from '@angular/core';
import { IReportDefinition } from '../../../shared/shared-models/reports/report-definition.interface';
import { SharedPdfReportService } from '../../../shared/shared-services/reports/shared-pdf-report.service';
import { IStockTake, IStockTakeFinalFigure, IStockTakeItemDetail } from '../models';
import {
  buildStockTakeSummaryReportDefinition,
  IBuildStockTakeSummaryReportOptions,
  mapStockTakeItemDetailsToReportFigures,
  StockTakeSummaryReportPhase,
} from '../utils/stock-take-report.utils';

/**
 * Report types for stock takes
 */
export enum StockTakeReportType {
  variance = 'variance',
  summary = 'summary',
}

/**
 * Service for generating stock take reports and CSV exports
 */
@Injectable({
  providedIn: 'root',
})
export class StockTakeReportsService {
  private readonly pdfReportService = inject(SharedPdfReportService);

  /**
   * Generate CSV export from final figures
   */
  generateFinalFiguresCsv(
    stockTake: IStockTake,
    figures: IStockTakeFinalFigure[],
  ): string {
    const headers = ['Code', 'Variance Qty'];

    const rows = figures.map((fig: IStockTakeFinalFigure) => [
      fig.code,
      fig.varianceQty.toString(),
    ]);

    const csvContent = [
      `Stock Take: ${stockTake.name}`,
      `Status: ${stockTake.status}`,
      `Started: ${stockTake.startTime.toDate().toLocaleString()}`,
      `Assigned To: ${stockTake.userAssigned}`,
      '',
      headers.join(','),
      ...rows.map((row: string[]) => row.join(',')),
    ].join('\n');

    return csvContent;
  }

  /**
   * Download a CSV string as a file
   */
  downloadCsv(filename: string, content: string): void {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Build a stock take summary PDF definition (defaults to all columns except latest cost).
   */
  buildSummaryPdfDefinition(
    stockTake: IStockTake,
    figures: IStockTakeFinalFigure[],
    options: IBuildStockTakeSummaryReportOptions = {},
  ): IReportDefinition {
    return buildStockTakeSummaryReportDefinition(stockTake, figures, options);
  }

  /**
   * Generate and open an A4 finalised stock take summary PDF in the browser.
   */
  async openSummaryPdf(
    stockTake: IStockTake,
    figures: IStockTakeFinalFigure[],
    options: IBuildStockTakeSummaryReportOptions = {},
  ): Promise<void> {
    const definition = this.buildSummaryPdfDefinition(stockTake, figures, {
      ...options,
      phase: options.phase ?? StockTakeSummaryReportPhase.finalised,
    });
    await this.pdfReportService.openReport(definition);
  }

  /**
   * Generate and open a pre-process draft summary PDF from live counted item details.
   */
  async openPreProcessSummaryPdf(
    stockTake: IStockTake,
    items: IStockTakeItemDetail[],
    options: IBuildStockTakeSummaryReportOptions = {},
  ): Promise<void> {
    const figures = mapStockTakeItemDetailsToReportFigures(items);
    await this.openSummaryPdf(stockTake, figures, {
      ...options,
      phase: StockTakeSummaryReportPhase.preProcess,
    });
  }
}
